import { LAB_EXPORT_SCHEMA } from "@/lib/labExport";
import { readManualText } from "@/lib/previewManual";

/** Reads a Reliv Labs export back in: only the run ref and the patient lines. */

export const NOT_A_LAB_EXPORT = "This file is not a Reliv Labs export.";
export const OTHER_PROJECT = "This conversation belongs to another project.";

export type LabImportLine = { turnNumber: number; text: string };

export type LabImport = {
  runRef: string;
  /** In turn order. */
  lines: LabImportLine[];
  /** Turns with no patient text in the file (withheld or never read). */
  skipped: number;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseLabExport(
  json: string,
  projectId: string,
): { ok: true; value: LabImport } | { ok: false; message: string } {
  const notAnExport = { ok: false, message: NOT_A_LAB_EXPORT } as const;
  let file: unknown;
  try {
    file = JSON.parse(json);
  } catch {
    return notAnExport;
  }
  if (!isRecord(file) || file["schema"] !== LAB_EXPORT_SCHEMA) {
    return notAnExport;
  }
  const { project, run, turns } = file;
  const runRef = isRecord(run) ? run["ref"] : undefined;
  if (
    !isRecord(project) ||
    typeof project["id"] !== "string" ||
    typeof runRef !== "string" ||
    runRef === "" ||
    !Array.isArray(turns)
  ) {
    return notAnExport;
  }
  if (project["id"] !== projectId) {
    return { ok: false, message: OTHER_PROJECT };
  }
  const lines: LabImportLine[] = [];
  let skipped = 0;
  for (const turn of turns) {
    const turnNumber = isRecord(turn) ? turn["turnNumber"] : undefined;
    if (typeof turnNumber !== "number") {
      return notAnExport;
    }
    const patient =
      isRecord(turn) && isRecord(turn["patient"]) ? turn["patient"] : null;
    const text =
      typeof patient?.["text"] === "string"
        ? readManualText(patient["text"])
        : null;
    if (text === null) {
      skipped += 1;
    } else {
      lines.push({ turnNumber, text });
    }
  }
  lines.sort((a, b) => a.turnNumber - b.turnNumber);
  return { ok: true, value: { runRef, lines, skipped } };
}
